import { Component, OnInit } from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { AhmedElshahatJobService } from '../../../../services/ahmed-elshahat-job';
import { DoctorsService } from '../../../../services/doctors';
import { JobApplicationService } from '../../../../services/job-application';
import { UserService } from '../../../../services/user';
import Swal from 'sweetalert2';

const STATUS_LABELS: Record<string, string> = {
  pending: 'قيد المراجعة',
  reviewed: 'تمت المراجعة',
  shortlisted: 'قائمة مختصرة',
  pending_review: 'قيد المراجعة',
  contacted: 'تم التواصل',
  interview_scheduled: 'تم تحديد مقابلة',
  interview_done: 'تمت المقابلة',
  documents_completion: 'استكمال المستندات',
  client_review: 'مراجعة العميل',
  client_approved: 'موافقة العميل',
  accepted: 'مقبول',
  in_process: 'جاري التنفيذ',
  medical_check: 'الكشف الطبي',
  travel_preparation: 'تجهيز السفر',
  hired: 'تم التعيين',
  traveled: 'سافر',
  rejected: 'مرفوض',
  rejected_by_client: 'مرفوض من العميل',
  postponed: 'مؤجل',
  on_hold: 'معلق',
  no_response: 'لا يوجد رد',
  not_qualified: 'غير مؤهل',
  waiting_for_data: 'بانتظار بيانات',
  cancelled: 'ملغي',
  withdrawn: 'منسحب',
};

@Component({
  selector: 'app-apply',
  templateUrl: './apply.component.html',
  styleUrl: './apply.component.scss',
  providers: [
    {
      provide: MatPaginatorIntl,
      useFactory: getArabicPaginatorIntl,
    },
  ],
})
export class ApplyComponent implements OnInit {
  contentReady = false;
  noData = false;
  isUpdating = false;
  hasContactAccess = false;

  dataSource = new MatTableDataSource<any>([]);
  private readonly baseDisplayedColumns: string[] = [
    'select',
    'applicant',
    'job',
    'contact',
    'status',
    'cv',
    'created_at',
    'options',
  ];
  displayedColumns: string[] = [];

  selectedApplicationIds = new Set<number | string>();
  actionPopupApplication: any = null;
  jobPopupApplication: any = null;
  companyPopupApplication: any = null;
  bulkPopupOpen = false;

  jobs: any[] = [];
  totalItems = 0;
  currentPage = 1;
  perPage = 10;

  stats = {
    total: 0,
    pending: 0,
    accepted: 0,
    rejected: 0,
  };

  filters: any = {
    search: '',
    status: '',
    job_id: '',
    page: 1,
    per_page: 10,
  };

  statuses = [
    'pending_review',
    'contacted',
    'interview_scheduled',
    'interview_done',
    'documents_completion',
    'client_review',
    'client_approved',
    'accepted',
    'in_process',
    'medical_check',
    'travel_preparation',
    'hired',
    'traveled',
    'rejected',
    'rejected_by_client',
    'postponed',
    'on_hold',
    'no_response',
    'not_qualified',
    'waiting_for_data',
    'cancelled',
    'withdrawn',
  ];

  constructor(
    private applicationService: JobApplicationService,
    private doctorService: DoctorsService,
    private jobService: AhmedElshahatJobService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.loadUserAccess();
  }

  loadApplications(): void {
    this.contentReady = false;
    this.noData = false;

    this.filters.page = this.currentPage;
    this.filters.per_page = this.perPage;

    this.applicationService.getJobApplications(this.filters).subscribe({
      next: (res: any) => {
        const payload = res?.data ?? res?.applications ?? res;
        const list = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : [];

        this.dataSource.data = list;
        this.syncSelectionWithPage();
        this.currentPage = payload?.current_page || res?.current_page || this.currentPage;
        this.perPage = Number(payload?.per_page || res?.per_page || this.perPage);
        this.totalItems = payload?.total || res?.total || list.length;
        this.stats.total = this.totalItems;
        this.calculatePageStats(list);

        this.noData = list.length === 0;
        this.contentReady = true;
      },
      error: (err: any) => {
        console.error('Get Job Applications Error:', err);
        this.dataSource.data = [];
        this.totalItems = 0;
        this.noData = true;
        this.contentReady = true;
        alert('حدث خطأ أثناء تحميل التقديمات');
      },
    });
  }

  loadJobs(): void {
    this.jobService.getJobs({ per_page: 1000 }).subscribe({
      next: (res: any) => {
        const payload = res?.data ?? res;
        this.jobs = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : [];
      },
      error: (err: any) => {
        console.error('Get Jobs For Applications Error:', err);
      },
    });
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.loadApplications();
  }

  resetFilters(): void {
    this.filters = {
      search: '',
      status: '',
      job_id: '',
      page: 1,
      per_page: this.perPage,
    };
    this.currentPage = 1;
    this.loadApplications();
  }

  refresh(): void {
    this.loadApplications();
    this.loadJobs();
  }

  onPaginateChange(event: any): void {
    this.currentPage = event.pageIndex + 1;
    this.perPage = event.pageSize;
    this.loadApplications();
  }

  openActionPopup(application: any): void {
    this.actionPopupApplication = application;
    this.bulkPopupOpen = false;
  }

  closeActionPopup(): void {
    this.actionPopupApplication = null;
  }

  openJobPopup(application: any): void {
    this.jobPopupApplication = application;
    this.closeActionPopup();
    this.closeBulkPopup();
  }

  closeJobPopup(): void {
    this.jobPopupApplication = null;
  }

  openCompanyPopup(application: any): void {
    this.companyPopupApplication = application;
  }

  closeCompanyPopup(): void {
    this.companyPopupApplication = null;
  }

  openBulkPopup(): void {
    if (!this.selectedApplicationIds.size) return;

    this.bulkPopupOpen = true;
    this.actionPopupApplication = null;
  }

  closeBulkPopup(): void {
    this.bulkPopupOpen = false;
  }

  changeStatus(application: any, status: string): void {
    if (!application?.id || this.isUpdating) return;

    const oldStatus = application.status;
    application.status = status;
    this.isUpdating = true;

    this.applicationService.updateApplicationStatus(application.id, { status }).subscribe({
      next: () => {
        this.isUpdating = false;
        this.closeActionPopup();
        this.loadApplications();
      },
      error: (err: any) => {
        console.error('Change Application Status Error:', err);
        application.status = oldStatus;
        this.isUpdating = false;
        alert('حدث خطأ أثناء تغيير حالة التقديم');
      },
    });
  }

  changeBulkStatus(status: string): void {
    const selectedApplications = this.getSelectedApplications();

    if (!selectedApplications.length || this.isUpdating) return;

    this.isUpdating = true;
    let completed = 0;
    let failed = false;

    selectedApplications.forEach((application) => {
      this.applicationService.updateApplicationStatus(application.id, { status }).subscribe({
        next: () => {
          completed += 1;
          this.finishBulkStatusChange(completed, selectedApplications.length, failed);
        },
        error: (err: any) => {
          console.error('Bulk Change Application Status Error:', err);
          completed += 1;
          failed = true;
          this.finishBulkStatusChange(completed, selectedApplications.length, failed);
        },
      });
    });
  }

  deleteApplication(application: any): void {
    if (!application?.id) return;

    const confirmed = confirm(`هل أنت متأكد من حذف تقديم #${application.id}؟`);
    if (!confirmed) return;

    this.applicationService.deleteJobApplication(application.id).subscribe({
      next: () => {
        this.dataSource.data = this.dataSource.data.filter((item) => item.id !== application.id);
        this.selectedApplicationIds.delete(application.id);
        this.totalItems = Math.max(this.totalItems - 1, 0);
        this.noData = this.dataSource.data.length === 0;
        this.closeActionPopup();
      },
      error: (err: any) => {
        console.error('Delete Application Error:', err);
        alert('حدث خطأ أثناء حذف التقديم');
      },
    });
  }

  convertApplicationToDoctor(application: any): void {
    this.convertApplicationToTalent(application, true);
  }

  convertApplicationToKader(application: any): void {
    this.convertApplicationToTalent(application, false);
  }

  toggleApplicationSelection(application: any): void {
    if (!application?.id) return;

    if (this.selectedApplicationIds.has(application.id)) {
      this.selectedApplicationIds.delete(application.id);
      return;
    }

    this.selectedApplicationIds.add(application.id);
  }

  isApplicationSelected(application: any): boolean {
    return Boolean(application?.id && this.selectedApplicationIds.has(application.id));
  }

  togglePageSelection(): void {
    const rows = this.dataSource.data.filter((item) => item?.id);
    const allSelected = rows.length > 0 && rows.every((item) => this.selectedApplicationIds.has(item.id));

    rows.forEach((item) => {
      if (allSelected) {
        this.selectedApplicationIds.delete(item.id);
      } else {
        this.selectedApplicationIds.add(item.id);
      }
    });
  }

  isPageSelected(): boolean {
    const rows = this.dataSource.data.filter((item) => item?.id);
    return rows.length > 0 && rows.every((item) => this.selectedApplicationIds.has(item.id));
  }

  isPagePartiallySelected(): boolean {
    const rows = this.dataSource.data.filter((item) => item?.id);
    const selectedCount = rows.filter((item) => this.selectedApplicationIds.has(item.id)).length;
    return selectedCount > 0 && selectedCount < rows.length;
  }

  selectedCount(): number {
    return this.selectedApplicationIds.size;
  }

  clearSelection(): void {
    this.selectedApplicationIds.clear();
    this.closeBulkPopup();
  }

  getApplicantName(application: any): string {
    const applicant = this.getApplicant(application);

    return (
      applicant?.name ||
      [applicant?.first_name, applicant?.last_name].filter(Boolean).join(' ') ||
      application?.name ||
      application?.applicant_name ||
      'بدون اسم'
    );
  }

  getApplicantEmail(application: any): string {
    if (!this.hasContactAccess) return '-';

    const applicant = this.getApplicant(application);
    return applicant?.email || application?.email || application?.applicant_email || '-';
  }

  getApplicantPhone(application: any): string {
    if (!this.hasContactAccess) return '-';

    const applicant = this.getApplicant(application);
    return applicant?.phone || applicant?.whatsapp || application?.phone || application?.whatsapp || '-';
  }

  getJobTitle(application: any): string {
    const job = this.getJob(application);

    return (
      job?.title_ar ||
      job?.title_en ||
      job?.title ||
      application?.job_title ||
      this.getJobTitleFromList(application?.job_id) ||
      'وظيفة غير محددة'
    );
  }

  getJobLocation(application: any): string {
    const job = this.getJob(application);
    const city = job?.city?.city_name_ar || job?.city?.city_name_en || job?.city_name_ar || job?.city_name_en;
    const country = job?.country?.country_name_ar || job?.country?.country_name_en || job?.country_name_ar || job?.country_name_en;

    return [city, country].filter(Boolean).join('، ') || 'غير محدد';
  }

  getJobSummary(application: any): any {
    return this.getJob(application) || {};
  }

  hasCompanyData(application: any): boolean {
    const company = this.getCompany(application);

    return Boolean(
      company?.name ||
      company?.name_ar ||
      company?.name_en ||
      company?.company_name ||
      company?.company_name_ar ||
      company?.company_name_en ||
      company?.tel ||
      company?.phone ||
      company?.contact_email ||
      company?.address ||
      this.getJob(application)?.company_name ||
      this.getJob(application)?.company_name_ar ||
      this.getJob(application)?.company_name_en
    );
  }

  getCompanyName(application: any): string {
    const company = this.getCompany(application);
    const job = this.getJob(application);

    return (
      company?.name_ar ||
      company?.name_en ||
      company?.name ||
      company?.company_name_ar ||
      company?.company_name_en ||
      company?.company_name ||
      job?.company_name_ar ||
      job?.company_name_en ||
      job?.company_name ||
      'شركة غير محددة'
    );
  }

  getCompanySpecialty(application: any): string {
    const company = this.getCompany(application);
    return company?.specialty || company?.industry || company?.field || 'غير محدد';
  }

  getCompanyAddress(application: any): string {
    const company = this.getCompany(application);
    return company?.address || company?.location || company?.city || 'غير محدد';
  }

  getCompanyPhone(application: any): string {
    if (!this.hasContactAccess) return '-';

    const company = this.getCompany(application);
    return company?.tel || company?.phone || company?.mobile || '-';
  }

  getCompanyEmail(application: any): string {
    if (!this.hasContactAccess) return '-';

    const company = this.getCompany(application);
    return company?.contact_email || company?.email || '-';
  }

  getCompanyWebsite(application: any): string {
    const company = this.getCompany(application);
    return company?.website || company?.website_url || company?.linkedin || company?.facebook || '';
  }

  getJobSalary(application: any): string {
    const job = this.getJob(application);
    if (!job) return 'غير محدد';

    const currency = job.salary_currency || '';

    if (job.salary_type === 'competitive') return 'راتب تنافسي';
    if (job.salary_type === 'negotiable') return job.salary_note || 'قابل للتفاوض';

    if (job.salary_type === 'fixed') {
      return `${job.salary_min || job.salary_max || 'غير محدد'} ${currency}`.trim();
    }

    if (job.salary_type === 'range') {
      return `${job.salary_min || '-'} - ${job.salary_max || '-'} ${currency}`.trim();
    }

    return job.salary_note || 'غير محدد';
  }

  getJobType(application: any): string {
    const job = this.getJob(application);
    const workModelMap: Record<string, string> = {
      on_site: 'من مقر العمل',
      remote: 'عن بعد',
      hybrid: 'هايبرد',
    };
    const employmentMap: Record<string, string> = {
      full_time: 'دوام كامل',
      part_time: 'دوام جزئي',
      contract: 'تعاقد',
      internship: 'تدريب',
    };

    return [
      workModelMap[job?.work_model] || job?.work_model,
      employmentMap[job?.employment_type] || job?.employment_type,
    ].filter(Boolean).join(' - ') || 'غير محدد';
  }

  getStatusLabel(status: string): string {
    const normalized = this.normalizeStatus(status);
    return STATUS_LABELS[normalized] || this.humanizeStatus(normalized);
  }

  getStatusClass(status: string): string {
    return `status-${this.normalizeStatus(status).replace(/_/g, '-')}`;
  }

  getStatusIcon(status: string): string {
    const normalized = this.normalizeStatus(status);

    if (['accepted', 'client_approved', 'hired', 'traveled'].includes(normalized)) {
      return 'fa-circle-check';
    }

    if (['rejected', 'rejected_by_client', 'not_qualified', 'cancelled', 'withdrawn'].includes(normalized)) {
      return 'fa-circle-xmark';
    }

    if (['interview_scheduled', 'interview_done'].includes(normalized)) {
      return 'fa-calendar-check';
    }

    if (['contacted', 'no_response'].includes(normalized)) {
      return 'fa-phone-volume';
    }

    if (['documents_completion', 'waiting_for_data'].includes(normalized)) {
      return 'fa-folder-open';
    }

    if (['client_review', 'in_process', 'medical_check', 'travel_preparation'].includes(normalized)) {
      return 'fa-spinner';
    }

    if (['postponed', 'on_hold'].includes(normalized)) {
      return 'fa-pause-circle';
    }

    return 'fa-hourglass-half';
  }

getCvLink(application: any): string {
  if (!this.hasContactAccess) return '';

  const applicant = this.getApplicant(application);

  const raw =
    application?.cv_url ||
    application?.cv ||
    application?.resume_url ||
    application?.resume ||
    application?.file_url ||
    application?.file ||
    application?.attachment_url ||
    application?.attachment ||
    applicant?.cv_url ||
    applicant?.cv ||
    applicant?.resume_url ||
    applicant?.resume ||
    applicant?.file_url ||
    '';

  return this.fixStorageUrl(raw);
}

private readonly backendStorageBaseUrl =
  'https://backendtest.blueboxpet.com/storage/app/public';

fixStorageUrl(url: any): string {
  if (!url) return '';

  let path =
    url?.url ||
    url?.file_url ||
    url?.path ||
    url?.file ||
    url?.cv ||
    url;

  path = String(path).trim();

  if (!path || path === 'null' || path === 'undefined') return '';

  // لو لينك خارجي كامل ومش من السيرفر بتاعنا، سيبه زي ما هو
  if (/^https?:\/\//i.test(path) && !/blueboxpet\.com/i.test(path)) {
    return path;
  }

  path = path
    .replace(/^https?:\/\/backendtest\.blueboxpet\.com\//i, '')
    .replace(/^https?:\/\/backendproduction\.blueboxpet\.com\//i, '')
    .replace(/^\/+/, '');

  path = path
    .replace(/^storage\/app\/public\//, '')
    .replace(/^storage\/app\//, '')
    .replace(/^storage\/public\//, '')
    .replace(/^storage\//, '')
    .replace(/^app\/public\//, '')
    .replace(/^public\//, '');

  return `${this.backendStorageBaseUrl}/${path}`;
}

  formatDate(date: any): string {
    if (!date) return 'غير محدد';

    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return 'غير محدد';

    return parsed.toLocaleString('ar-EG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  toArabicNumber(value: any): string {
    if (value === null || value === undefined) return '';

    return value.toString().replace(/\d/g, (digit: string) => {
      return '٠١٢٣٤٥٦٧٨٩'[Number(digit)];
    });
  }

  trackByApplicationId(index: number, application: any): number | string {
    return application?.id || index;
  }

  private getApplicant(application: any): any {
    return application?.user || application?.doctor || application?.applicant || application?.candidate || null;
  }

  private loadUserAccess(): void {
    this.contentReady = false;

    this.userService.getAccountType().subscribe({
      next: (data: any) => {
        this.hasContactAccess = this.toAccessBoolean(data?.has_access_to_phone_numbers);
        this.setDisplayedColumnsByAccess();
        this.loadJobs();
        this.loadApplications();
      },
      error: (err: any) => {
        console.error('Get User Access Error:', err);
        this.hasContactAccess = false;
        this.setDisplayedColumnsByAccess();
        this.loadJobs();
        this.loadApplications();
      },
    });
  }

  private setDisplayedColumnsByAccess(): void {
    const hiddenColumns = this.hasContactAccess ? [] : ['contact', 'cv'];
    this.displayedColumns = this.baseDisplayedColumns.filter((column) => {
      return !hiddenColumns.includes(column);
    });
  }

  private toAccessBoolean(value: any): boolean {
    return value === true || value === 1 || value === '1';
  }

  private getJob(application: any): any {
    return application?.job || application?.ahmed_elshahat_job || application?.job_data || null;
  }

  private getCompany(application: any): any {
    const job = this.getJob(application);
    return job?.company || job?.company_data || application?.company || application?.company_data || {};
  }

  private convertApplicationToTalent(application: any, isDoctor: boolean): void {
    if (!application?.id || this.isUpdating) return;

    const targetLabel = isDoctor ? 'دكتور' : 'كادر';
    const confirmed = confirm(`هل تريد تحويل هذا التقديم إلى ${targetLabel}؟`);
    if (!confirmed) return;

    const payload = this.buildTalentPayload(application, isDoctor);
    this.isUpdating = true;

    this.doctorService.create(payload).subscribe({
      next: () => {
        this.isUpdating = false;
        this.closeActionPopup();
        Swal.fire({
          icon: 'success',
          title: `تم تحويل التقديم إلى ${targetLabel}`,
          showConfirmButton: false,
          timer: 1500,
        });
      },
      error: (err: any) => {
        this.isUpdating = false;
        console.error('Convert Application Error:', err);
        Swal.fire({
          icon: 'error',
          title: err?.error?.message || `حدث خطأ أثناء تحويل التقديم إلى ${targetLabel}`,
          confirmButtonText: 'تمام',
        });
      },
    });
  }

  private buildTalentPayload(application: any, isDoctor: boolean): FormData {
    const applicant = this.getApplicant(application);
    const job = this.getJob(application);
    const formData = new FormData();

    const append = (key: string, value: any) => {
      if (value !== null && value !== undefined && value !== '') {
        formData.append(key, String(value));
      }
    };

    append('name', this.getApplicantName(application));
    append('email', this.hasContactAccess ? this.getApplicantEmail(application) : '');
    append('mobile', this.hasContactAccess ? this.getApplicantPhone(application) : '');
    append('specialty', job?.specialty || job?.title_ar || job?.title_en || application?.job_title || '');
    append('qualification', applicant?.qualification || application?.qualification || '');
    append('university', applicant?.university || application?.university || '');
    append('graduation_year', applicant?.graduation_year || application?.graduation_year || '');
    append('residence_place', applicant?.residence_place || application?.residence_place || this.getJobLocation(application));
    append('birth_place', applicant?.birth_place || application?.birth_place || '');
    append('ksa_experience', applicant?.ksa_experience ? '1' : '0');
    append('is_doctor', isDoctor ? '1' : '0');
    append('add_by', localStorage.getItem('username') || 'System');
    append('notes', this.buildTalentNotes(application, isDoctor));

    return formData;
  }

  private buildTalentNotes(application: any, isDoctor: boolean): string {
    const target = isDoctor ? 'دكتور' : 'كادر';
    const notes = [
      `تم تحويله من تقديم الوظائف إلى ${target}.`,
      `رقم التقديم: ${application?.id || '-'}`,
      `الوظيفة: ${this.getJobTitle(application)}`,
      `الحالة: ${this.getStatusLabel(application?.status)}`,
      application?.cover_letter ? `رسالة المتقدم: ${application.cover_letter}` : '',
      application?.notes ? `ملاحظات التقديم: ${application.notes}` : '',
    ];

    return notes.filter(Boolean).join('\n');
  }

  private getJobTitleFromList(jobId: any): string {
    if (!jobId) return '';

    const job = this.jobs.find((item) => item.id == jobId);
    return job?.title_ar || job?.title_en || job?.title || '';
  }

  private calculatePageStats(list: any[]): void {
    this.stats.pending = list.filter((item) => {
      return ['pending', 'pending_review', 'waiting_for_data'].includes(this.normalizeStatus(item?.status));
    }).length;

    this.stats.accepted = list.filter((item) => {
      return ['accepted', 'client_approved', 'hired', 'traveled'].includes(this.normalizeStatus(item?.status));
    }).length;

    this.stats.rejected = list.filter((item) => {
      return ['rejected', 'rejected_by_client', 'not_qualified'].includes(this.normalizeStatus(item?.status));
    }).length;
  }

  private getSelectedApplications(): any[] {
    return this.dataSource.data.filter((item) => item?.id && this.selectedApplicationIds.has(item.id));
  }

  private syncSelectionWithPage(): void {
    const pageIds = new Set(this.dataSource.data.map((item) => item?.id).filter(Boolean));

    Array.from(this.selectedApplicationIds).forEach((id) => {
      if (!pageIds.has(id)) {
        this.selectedApplicationIds.delete(id);
      }
    });
  }

  private finishBulkStatusChange(completed: number, total: number, failed: boolean): void {
    if (completed < total) return;

    this.isUpdating = false;
    this.closeBulkPopup();
    this.clearSelection();

    if (failed) {
      alert('تم تحديث بعض التقديمات، لكن حصل خطأ في جزء منها');
    }

    this.loadApplications();
  }

  private normalizeStatus(status: any): string {
    const value = String(status || 'pending_review').trim().toLowerCase();

    if (value === 'pending') return 'pending_review';
    if (value === 'reviewed') return 'contacted';
    if (value === 'shortlisted') return 'client_review';

    return value;
  }

  private humanizeStatus(status: string): string {
    return status.replace(/_/g, ' ');
  }
}

export function getArabicPaginatorIntl(): MatPaginatorIntl {
  const paginatorIntl = new MatPaginatorIntl();

  const toArabicNumber = (value: any): string => {
    if (value === null || value === undefined) return '';

    return value.toString().replace(/\d/g, (digit: string) => {
      return '٠١٢٣٤٥٦٧٨٩'[Number(digit)];
    });
  };

  paginatorIntl.itemsPerPageLabel = 'عدد العناصر في الصفحة';
  paginatorIntl.nextPageLabel = 'الصفحة التالية';
  paginatorIntl.previousPageLabel = 'الصفحة السابقة';
  paginatorIntl.firstPageLabel = 'أول صفحة';
  paginatorIntl.lastPageLabel = 'آخر صفحة';

  paginatorIntl.getRangeLabel = (page: number, pageSize: number, length: number): string => {
    if (length === 0 || pageSize === 0) {
      return `٠ من ${toArabicNumber(length)}`;
    }

    const startIndex = page * pageSize;
    const endIndex = Math.min(startIndex + pageSize, length);

    return `${toArabicNumber(startIndex + 1)} - ${toArabicNumber(endIndex)} من ${toArabicNumber(length)}`;
  };

  return paginatorIntl;
}
